import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FilePreviewService } from '../../../services/file-preview.service';
import { FileBadge, FileUtilityService } from '../../../services/file-utility.service';
import { FileDropzoneComponent } from '../../file-dropzone/file-dropzone.component';

interface SelectedFile {
  file: File;
  previewUrl?: string;
  badge?: FileBadge;
}

@Component({
  selector: 'app-attachment-upload-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./attachment-upload-modal.component.scss'],
  imports: [CommonModule, FileDropzoneComponent],
  templateUrl: './attachment-upload-modal.component.html',
})
export class AttachmentUploadModalComponent implements OnDestroy {
  readonly activeModal = inject(NgbActiveModal);
  private readonly fileUtilityService = inject(FileUtilityService);
  private readonly filePreviewService = inject(FilePreviewService);

  readonly files = signal<SelectedFile[]>([]);

  readonly hasFiles = computed(() => this.files().length > 0);

  onFilesSelected(incoming: File[]): void {
    const next = [
      ...this.files(),
      ...incoming.map((file) => ({
        file,
        previewUrl: this.filePreviewService.createPreviewUrl(file),
        badge: this.fileUtilityService.getFileBadge(file),
      })),
    ];
    this.files.set(next);
  }

  removeFile(entry: SelectedFile): void {
    this.filePreviewService.revokePreviewUrl(entry.previewUrl);
    this.files.set(this.files().filter((existing) => existing !== entry));
  }

  clearFiles(): void {
    this.files().forEach((entry) => this.filePreviewService.revokePreviewUrl(entry.previewUrl));
    this.files.set([]);
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  onNext(): void {
    if (!this.hasFiles()) {
      return;
    }

    this.activeModal.close(this.files().map((entry) => entry.file));
  }

  ngOnDestroy(): void {
    this.files().forEach((entry) => this.filePreviewService.revokePreviewUrl(entry.previewUrl));
  }
}
